import React from 'react';
import { CiCircleCheck } from 'react-icons/ci';
import { GoPersonAdd } from 'react-icons/go';
import { RiLoader2Line } from 'react-icons/ri';
import { TbListCheck } from 'react-icons/tb';

interface DashboardStatusRowProps {
  unassignedCount: number;
  assignedCount: number;
  inProgressCount: number;
  reviewCount: number;
  deliveredCount: number;
  totalProjects: number;
  onStatusClick: (status: string, index: number) => void;
  loading?: boolean;
}

const DashboardStatusRow: React.FC<DashboardStatusRowProps> = ({
  unassignedCount,
  assignedCount,
  inProgressCount,
  reviewCount,
  deliveredCount,
  onStatusClick,
  loading = false,
}) => {
  // Define the five status cards configuration
  const statusCards = [
    {
      key: 'UNASSIGNED',
      label: 'Un-Assigned',
      icon: null,
      bgColor: 'bg-white',
      borderColor: 'border-neutral-50',
      headerBgGradient: 'bg-[#f9f9f9]',
      titleColor: 'text-[#a0a0a0]',
      valueColor: 'text-[#a0a0a0]',
      count: unassignedCount,
      index: 0,
    },
    {
      key: 'ASSIGNED',
      label: 'Assigned',
      icon: <GoPersonAdd className='w-full h-full' />,
      bgColor: 'bg-white',
      borderColor: 'border-[#f2f6ff]',
      headerBgGradient: 'bg-[#e8f0ff]',
      titleColor: 'text-[#5f8cff]',
      valueColor: 'text-[#5f8cff]',
      count: assignedCount,
      index: 1,
    },
    {
      key: 'IN_PROGRESS',
      label: 'In-Progress',
      icon: <RiLoader2Line className='w-full h-full' />,
      bgColor: 'bg-white',
      borderColor: 'border-[#e9eaff]',
      headerBgGradient: 'bg-[#dcdeff]',
      titleColor: 'text-[#2c2f61]',
      valueColor: 'text-[#2c2f61]',
      count: inProgressCount,
      index: 2,
    },
    {
      key: 'REVIEW',
      label: 'Review',
      icon: <TbListCheck className='w-full h-full' />,
      bgColor: 'bg-white',
      borderColor: 'border-[#fff3d1]',
      headerBgGradient: 'bg-[#ffebb3]',
      titleColor: 'text-[#d7a205]',
      valueColor: 'text-[#d7a205]',
      count: reviewCount,
      index: 3,
    },
    {
      key: 'DELIVERED',
      label: 'Delivered',
      icon: <CiCircleCheck className='w-full h-full' />,
      bgColor: 'bg-white',
      borderColor: 'border-[#a5ffa8]',
      headerBgGradient: 'bg-[#c4ffc6]',
      titleColor: 'text-[#13c018]',
      valueColor: 'text-[#13c018]',
      count: deliveredCount,
      index: 4,
    },
  ];

  if (loading) {
    return (
      <div className="mb-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-gray-200 shadow-sm animate-pulse"
            >
              <div className="p-6">
                <div className="w-8 h-8 bg-gray-200 rounded mb-3"></div>
                <div className="h-4 w-20 bg-gray-200 rounded mb-4"></div>
                <div className="h-10 w-16 bg-gray-200 rounded mb-2"></div>
                <div className="h-3 w-12 bg-gray-200 rounded"></div>
              </div>
              <div className="h-10 bg-gray-100 rounded-b-2xl"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-10">
      {/* Heading */}
      <div className="flex items-center gap-4 mb-8">
        <div className='w-10 h-10'>
          <RiLoader2Line className='h-full w-full' />
        </div>
        <h2 className="font-semibold text-[#333333] text-[24px] tracking-[-0.64px]">
          Project Status
        </h2>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {statusCards.map((card) => (
          <button
            key={card.key}
            onClick={() => onStatusClick(card.key, card.index)}
            className={`
              flex flex-col
              ${card.bgColor} ${card.borderColor}
              rounded-2xl border
              shadow-[0px_2px_7px_rgba(0,0,0,0.08)]
              overflow-hidden
              transition-all duration-200
              cursor-pointer 
              hover:shadow-xl hover:-translate-y-1
              text-left
            `}
          >
            {/* Header with gradient background */}
            <div className={`relative h-[44px] ${card.headerBgGradient} flex items-center px-6 gap-2 min-w-0`}>
              {card.icon && (
                <div className={`w-6 h-6 flex-shrink-0 ${card.titleColor}`}>
                  {card.icon}
                </div>
              )}
              <h3 className={`font-semibold ${card.titleColor} text-sm sm:text-base lg:text-lg xl:text-xl tracking-[-0.48px] truncate`}>
                {card.label}
              </h3>
            </div>

            {/* Count */}
            <div className="px-6 pb-6 pt-2">
              <p className={`font-extrabold ${card.valueColor} text-4xl tracking-[-0.72px]`}>
                {card.count.toLocaleString()}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default DashboardStatusRow;
